import { COMMERCIAL_TECHNOLOGIES, WONDER_PLACEMENT_COST } from '../constants/commercial';
import { CITY_FRANCHISE_INFLUENCE } from './airports';
import { OpCode, MessageType } from '../constants/op-codes';
/** Private commercial observations are replaced as one owning-team snapshot. */
import { create } from 'zustand';
import type { Team } from './teams';
import { handleTeamUpdate } from './teams';
import { sendGatedCommand } from './command-gate';
import { getCurrentCohortId, getViewerUserId, useCurrentCohortId } from './current-team';
import { showErrorToast } from './toasts';
import { budgetBlock, cashBlock, refusalKey, refusalText, type OrderBlock, type OrderRefusal } from './order-block';
import { cityCode, cityName } from '../data/cities';
import { formatCash, formatDecimal } from '../utils/format';
import i18n from '../i18n/config';

export type Persona = 'road_warriors' | 'families' | 'premium_travelers' | 'value_travelers';
export const PERSONAS: Persona[] = ['road_warriors', 'families', 'premium_travelers', 'value_travelers'];

/** Each persona's name: its key, written whole. */
export const PERSONA_WORDS: Record<Persona, string> = {
  road_warriors: 'fourX.personas.road_warriors',
  families: 'fourX.personas.families',
  premium_travelers: 'fourX.personas.premium_travelers',
  value_travelers: 'fourX.personas.value_travelers',
};

export type CommercialAction = 'survey' | 'assign_advisor' | 'advisor_study' | 'place_wonder' | 'wonder_census' | 'claim_contract';

/** Each action's title when the server refuses it: its key, written whole. */
const FAILED_WORDS: Record<CommercialAction, string> = {
  survey: 'fourX.failed.survey',
  assign_advisor: 'fourX.failed.assign_advisor',
  advisor_study: 'fourX.failed.advisor_study',
  place_wonder: 'fourX.failed.place_wonder',
  wonder_census: 'fourX.failed.wonder_census',
  claim_contract: 'fourX.failed.claim_contract',
};

/**
 * Each source of a study — a Franchise's survey, the Advisor's study, the
 * Wonder's census — by the server's name for it (StudySource… in
 * commercial.go, which client_parity_test.go holds to these keys): its name,
 * a key written whole.
 */
export const SOURCE_WORDS = {
  survey: 'fourX.sources.survey',
  advisor: 'fourX.sources.advisor',
  wonder: 'fourX.sources.wonder',
} as const;
export type StudySource = keyof typeof SOURCE_WORDS;

/**
 * How a corporate contract stands, by the server's name for it
 * (ContractResult… in commercial.go, which client_parity_test.go holds to
 * these keys): available to claim or active since a claim, which say nothing
 * yet, or renewed or released at its last review, which a review's line names
 * by its key, written whole.
 */
export const CONTRACT_RESULT_WORDS = {
  available: null,
  active: null,
  renewed: 'fourX.contractResults.renewed',
  released: 'fourX.contractResults.released',
} as const;
type ContractResult = keyof typeof CONTRACT_RESULT_WORDS;
/**
 * What investing in a city would add to the team's network, as the server
 * works it out with the commercial state (investment-preview.go), for the city
 * panel's Next investment card: whether it makes the city a Base, the cities
 * that become starting points for onward routes, and the cities a first
 * investment could then reach in the fleet's range.
 */
export interface CityInvestmentReach {
  becomes_base: boolean;
  new_connection_origins: number[];
  new_destination_ids: number[];
  new_destination_count: number;
  range_km: number;
}
export interface MarketResearch {
  cycle: number;
  source: StudySource;
  sample_parties: number;
  daily_trips: { low: number; high: number };
  destinations: Array<{ city_id: number; daily_trips: { low: number; high: number }; captured_trips?: number; capture_percent?: number }>;
  tradeoffs?: { time_fare: number; comfort_fare: number };
}
export interface PersonaObservation {
  evidence: number;
  level: string;
  served_passengers: number;
  top_destination_id?: number;
  average_fare?: number;
  market?: MarketResearch;
}
/** The team's settled overflow dollars per day at one airport, with each
 * aircraft assignment's share keyed by segment and then flight. */
interface AirportOverflowCosts {
  daily_cost: number;
  flights: Record<number, Record<number, number>>;
}
export interface CommercialSnapshot {
  type: typeof MessageType.MessageTypeCommercialState;
  cohort_id: string;
  sender_user_id?: string;
  team: Team;
  cycle: number;
  cities: Record<number, { investment?: CityInvestmentReach; personas: Partial<Record<Persona, PersonaObservation>>; survey_ready_cycle: number }>;
  /** Only this team's costs. Older servers/saves omit them until settlement. */
  airport_costs?: Record<number, AirportOverflowCosts>;
  advisor: { available: boolean; city_id?: number; ready_cycle: number };
  wonder: { city_id: number; ready_cycle: number } | null;
  wonders: Record<number, number>;
  pending: Array<{ source: StudySource; city_id: number; persona?: Persona; completes_cycle: number }>;
  contracts: Array<{ contract_id: number; city_id: number; destination_id: number; owner_team_id: number; max_daily_trips: number; eligible: boolean; review_cycle: number; offered: number; fulfilled: number; demand_shortfall: number; last_result: ContractResult; last_offered?: number; last_fulfilled?: number; last_demand_shortfall?: number }>;
}
export const useCommercialStore = create<{
  snapshot: CommercialSnapshot | null;
  /** The action sent and not yet answered, which the city panel shows as under way. */
  pendingAction: CommercialAction | null;
  /** The city that action is in, which a refusal names. */
  pendingCityId: number | null;
  reset: () => void;
}>(set => ({
  snapshot: null,
  pendingAction: null,
  pendingCityId: null,
  reset: () => set({ snapshot: null, pendingAction: null, pendingCityId: null }),
}));
/** The current team's snapshot, never a snapshot left from a previous team. */
export function useTeamCommercialSnapshot(): CommercialSnapshot | null {
  const cohortId = useCurrentCohortId();
  return useCommercialStore((state) => state.snapshot?.cohort_id === cohortId ? state.snapshot : null);
}

export function handleCommercialState(snapshot: CommercialSnapshot) {
  const userId = getViewerUserId();
  const cohortId = getCurrentCohortId();
  if (!cohortId || cohortId !== snapshot.cohort_id) return;
  // The snapshot that names this player answers their action: its wait ends
  // here, as the gate opened on it (GATED_RESPONSES). Periodic and
  // teammates' snapshots are observations.
  const answersOurs = !!userId && snapshot.sender_user_id === userId;
  useCommercialStore.setState({ snapshot, ...(answersOurs ? { pendingAction: null, pendingCityId: null } : {}) });
  if (snapshot.team) handleTeamUpdate(snapshot.cohort_id, snapshot.team);
}

// ==================== REFUSALS ====================

/**
 * The reasons the server refuses a commercial action for, where a player can
 * meet them — commercial.go and commercial-messages.go send each as a code
 * beside the English — by the key that says each in the player's language.
 * Each is a teammate's or a rival's action, a step's lock, or the team's money
 * overtaking what the Market tab showed:
 *
 *   cash, budget     the team's cash or your budget short of the price (need
 *                    and have, in millions), said by cashBlock and budgetBlock
 *                    as the Buy button says them — their keys are listed so
 *                    the codes are named
 *   approval         too few on the Wonder's button (need and have)
 *   share            an approver short of an equal share of the Wonder (the
 *                    share, and the least an approver held)
 *   cooldown         a study the team shares, started first by a teammate
 *                    (the updates until it can run again, which the server
 *                    counts as `cycles`)
 *   frequency        a contract's route flown less often than it needs (need,
 *                    have, and the city it flies to: destination_id)
 *
 * and the Advisor moved, the airline's Wonder or account placed or claimed a
 * moment before, or a rival's landing first. A Go test reads this table
 * (commercial_refusal_test.go), so the two sides name the same codes.
 */
const COMMERCIAL_REFUSALS = {
  advisor_moved: 'fourX.refusal.advisorMoved',
  approval: 'cityAccess.refusal.approval',
  budget: 'common.budgetReason',
  cash: 'common.cashReason',
  cash_locked: 'common.cashLocked',
  contract_taken: 'fourX.refusal.contractTaken',
  cooldown: 'fourX.availability.cooldown',
  frequency: 'fourX.refusal.frequency',
  holds_contract: 'fourX.contractOneHere',
  owns_wonder: 'fourX.availability.ownsWonder',
  share: 'fourX.refusal.share',
  wonder_taken: 'fourX.refusal.wonderTaken',
} as const;

/**
 * What a refused commercial action tells the player: the key that says its
 * reason, with its figures — money in the player's number style, the updates
 * counted, a contract's destination named. Null when the server sent no reason
 * the client knows — a failure no player meets in play, or a code from a newer
 * server — or a reason that lost its figures, and the server's words stand.
 */
export function readCommercialRefusal({ reason, need, have, cycles, destination_id: destination }: OrderRefusal): OrderBlock | null {
  const key = refusalKey(COMMERCIAL_REFUSALS, reason);
  if (key === undefined) return null;
  switch (reason) {
    case 'cash':
    case 'budget':
      if (typeof need !== 'number' || typeof have !== 'number') return null;
      return reason === 'cash' ? cashBlock(have, need) : budgetBlock(have, need);
    case 'approval':
      if (typeof need !== 'number' || typeof have !== 'number') return null;
      return { key, values: { need, have } };
    case 'share':
      if (typeof need !== 'number' || typeof have !== 'number') return null;
      return { key, values: { need: formatCash(need), have: formatCash(have) } };
    case 'cooldown':
      return typeof cycles === 'number' ? { key, values: { count: cycles } } : null;
    case 'frequency':
      if (typeof need !== 'number' || typeof have !== 'number' || typeof destination !== 'number') return null;
      return { key, values: { count: need, need: formatDecimal(need), have: formatDecimal(have), city: cityName(cityCode(destination)) } };
    default:
      return { key };
  }
}

/**
 * A refused action: it is over, and a toast says which one failed and where,
 * and why — in the player's language when the server sent a reason the client
 * knows (readCommercialRefusal), else in the server's words. Nothing is
 * written into the panel — a failure passes.
 */
export function handleCommercialFailure(data: OrderRefusal) {
  console.warn('[Commercial] Refused:', data.reason ?? '', data.error);
  const { pendingAction: action, pendingCityId: cityId } = useCommercialStore.getState();
  useCommercialStore.setState({ pendingAction: null, pendingCityId: null });
  const title = action && cityId !== null
    ? i18n.t(FAILED_WORDS[action], { city: cityName(cityCode(cityId)) })
    : i18n.t('fourX.failed.action');
  showErrorToast(title, refusalText(readCommercialRefusal(data), data));
}
export async function commercialAction(action: CommercialAction, cityId: number, persona?: Persona, contractId?: number) {
  if (useCommercialStore.getState().pendingAction) return;
  useCommercialStore.setState({ pendingAction: action, pendingCityId: cityId });
  await sendGatedCommand(OpCode.OpCodeCommercialAction, JSON.stringify({ type: MessageType.MessageTypeCommercialAction, action, city_id: cityId, persona, contract_id: contractId }), {
    endWait: () => useCommercialStore.setState({ pendingAction: null, pendingCityId: null }),
    notSent: handleCommercialFailure,
  });
}

/**
 * Why the Wonder cannot be placed here, whoever joins, in the order its button
 * says it: a step that locks spending, the blueprint not researched, too
 * little Influence here, a Wonder your airline already has elsewhere. The
 * server checks all four (commercial.go, the spending lock).
 */
type WonderBlock = 'cashLocked' | 'blueprint' | 'franchise' | 'ownsWonder';

/** Each block's sentence: its key, written whole. */
export const WONDER_BLOCK_WORDS: Record<WonderBlock, string> = {
  cashLocked: 'common.cashLocked',
  blueprint: 'fourX.availability.blueprint',
  franchise: 'fourX.availability.franchise',
  ownsWonder: 'fourX.availability.ownsWonder',
};

export function wonderBlock({ cashLocked, unlockedInnovations, influence, ownsWonder }: {
  cashLocked: boolean;
  unlockedInnovations: Team['unlocked_innovations'];
  influence: number;
  ownsWonder: boolean;
}): WonderBlock | null {
  if (cashLocked) return 'cashLocked';
  if (!unlockedInnovations?.[COMMERCIAL_TECHNOLOGIES.wonder]) return 'blueprint';
  if (influence < CITY_FRANCHISE_INFLUENCE) return 'franchise';
  if (ownsWonder) return 'ownsWonder';
  return null;
}

/**
 * How the Wonder's price would be paid, as the server takes it
 * (DeductFromBudgetsCosign, budget.go): an equal share from each approver's
 * budget, all or nothing — so one approver short of a share stops it, however
 * much the others hold — and the team's cash must cover the whole price.
 * `covered` counts each budget up to the share, so a bar filled by it fills
 * only as far as the split works; `short` lists the approvers short of one.
 */
export function wonderFunding(approverBudgets: readonly number[], teamCash: number, cost = WONDER_PLACEMENT_COST) {
  const share = approverBudgets.length > 0 ? cost / approverBudgets.length : cost;
  return {
    share,
    covered: approverBudgets.reduce((sum, budget) => sum + Math.min(Math.max(budget, 0), share), 0),
    short: approverBudgets.flatMap((budget, index) => (budget < share ? [index] : [])),
    cashShort: teamCash < cost,
  };
}

/**
 * Preview only — the server rechecks all of it. The Wonder can be placed once
 * nothing blocks it, a majority of the team online is on it, each approver can
 * pay an equal share, and the team's cash covers the price.
 */
export function canPlaceMarketIntelligenceCenter({ block, approvals, requiredApprovals, approverBudgets, teamCash }: {
  block: WonderBlock | null;
  approvals: number;
  requiredApprovals: number;
  approverBudgets: readonly number[];
  teamCash: number;
}): boolean {
  if (block !== null || approvals < requiredApprovals) return false;
  const funding = wonderFunding(approverBudgets, teamCash);
  return funding.short.length === 0 && !funding.cashShort;
}
